// Treinamento da IA do WhatsApp: instrução, regras, o que a IA pode usar, comandos do bot
// e teste das respostas (sem WhatsApp). O backend lê esta configuração a cada mensagem.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiSend, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebaseConfig';
import Carregando from '../Carregando';
import { useBaseRelatorio } from '../relatorios/useRelatorios';
import { formatarTelefone, paraData, telefoneDoProgresso } from '../relatorios/util';
import './TreinamentoIA.css';

const apiUrl = process.env.REACT_APP_API_URL;
const REGRA_VAZIA = { quando: '', responder: '' };

function TreinamentoIA() {
  const tenantId = localStorage.getItem('tenantId');
  const [config, setConfig] = useState(null);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const { minicursos, progressos, nomesPorTelefone } = useBaseRelatorio(tenantId);

  const [mensagem, setMensagem] = useState('');
  const [aluno, setAluno] = useState('');
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);

  useEffect(() => {
    getDoc(doc(firestore, 'inquilinos', tenantId))
      .then((d) => {
        const x = d.exists() ? d.data() : {};
        setConfig({
          aiInstructionScript: x.aiInstructionScript || '',
          aiRegras: Array.isArray(x.aiRegras) && x.aiRegras.length ? x.aiRegras : [REGRA_VAZIA],
          aiUsarConteudoCursos: x.aiUsarConteudoCursos !== false,
          aiInformarDesempenho: x.aiInformarDesempenho !== false,
          aiPausaAtendimentoMin: x.aiPausaAtendimentoMin === undefined ? 10 : x.aiPausaAtendimentoMin,
        });
      })
      .catch((e) => {
        console.error('Treinamento da IA:', e);
        toast.error('Não foi possível carregar o treinamento da IA.');
      });
  }, [tenantId]);

  const mudar = (campo, valor) => {
    setConfig((c) => ({ ...c, [campo]: valor }));
    setAlterado(true);
  };
  const mudarRegra = (i, campo, valor) =>
    mudar('aiRegras', config.aiRegras.map((r, j) => (j === i ? { ...r, [campo]: valor } : r)));
  const removerRegra = (i) => {
    const restantes = config.aiRegras.filter((_, j) => j !== i);
    mudar('aiRegras', restantes.length ? restantes : [REGRA_VAZIA]);
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const regras = config.aiRegras
        .map((r) => ({ quando: r.quando.trim(), responder: r.responder.trim() }))
        .filter((r) => r.quando && r.responder);
      await setDoc(doc(firestore, 'inquilinos', tenantId), {
        aiInstructionScript: config.aiInstructionScript.trim(),
        aiRegras: regras,
        aiUsarConteudoCursos: config.aiUsarConteudoCursos,
        aiInformarDesempenho: config.aiInformarDesempenho,
        aiPausaAtendimentoMin: Math.max(0, Math.min(1440, Number(config.aiPausaAtendimentoMin) || 0)),
      }, { merge: true });
      setAlterado(false);
      return true;
    } catch (e) {
      console.error('Erro ao salvar treinamento:', e);
      toast.error('Não foi possível salvar o treinamento.');
      return false;
    } finally {
      setSalvando(false);
    }
  };

  // Alunos para simular no teste: um por contato, do mais recente para o mais antigo
  const alunos = useMemo(() => {
    if (!progressos || !minicursos) return [];
    const nomeDoCurso = (id) => minicursos.find((m) => m.id === id)?.nome || 'curso';
    const porContato = {};
    progressos.forEach((p) => {
      const atual = porContato[p.contactId];
      const quando = paraData(p.timestampUltimaEtapa)?.getTime() || 0;
      if (!atual || quando > atual.quando || (!p.concluido && atual.concluido)) {
        const tel = telefoneDoProgresso(p);
        porContato[p.contactId] = {
          contactId: p.contactId, quando, concluido: p.concluido,
          rotulo: `${p.nome || nomesPorTelefone[tel] || formatarTelefone(tel, p.contactId)} · ${p.concluido ? 'concluiu' : 'em'} ${nomeDoCurso(p.minicursoId)}`,
        };
      }
    });
    return Object.values(porContato).sort((a, b) => b.quando - a.quando).slice(0, 60);
  }, [progressos, minicursos, nomesPorTelefone]);

  const testar = async (e) => {
    e.preventDefault();
    if (!mensagem.trim()) return;
    if (alterado && !(await salvar())) return;
    setTestando(true);
    try {
      const r = await axios.post(`${apiUrl}/ia/testar`, { sanitizedClientId: tenantId, mensagem, contactId: aluno || null });
      setTeste({ pergunta: mensagem, resposta: r.data.resposta, categoria: r.data.categoria, acoes: r.data.acoes || [], prompt: r.data.prompt });
      setMensagem('');
    } catch (erro) {
      toast.error(erro.response?.data?.error || 'Não foi possível testar a IA agora.');
    } finally {
      setTestando(false);
    }
  };

  if (!config) return <Carregando texto="Carregando o treinamento da IA..." />;

  const ativos = (minicursos || []).filter((m) => m.status === 'ativo');

  return (
    <div className="qz-pagina ia-pagina">
      <header className="ia-cabecalho">
        <h2 className="qz-pagina__titulo">Treinamento da IA</h2>
        <p className="ia-suave">
          A IA responde no WhatsApp quando a mensagem não é um comando do curso (como "oi", "cursos" ou a letra de uma questão).
          Aqui você define como ela fala, o que fazer em situações específicas e o que ela pode usar. Vale na hora, sem religar o bot.
        </p>
      </header>

      <section className="qz-card ia-secao">
        <h3>Instrução principal</h3>
        <p className="ia-suave">Quem é o assistente, o tom de voz e o objetivo.</p>
        <textarea
          aria-label="Instrução principal"
          rows={5}
          value={config.aiInstructionScript}
          onChange={(e) => mudar('aiInstructionScript', e.target.value)}
          placeholder="Ex.: Você é o assistente da Escola X. Fale de forma simpática e objetiva, incentive o aluno a concluir o curso e não fale de assuntos fora do treinamento."
        />
      </section>

      <section className="qz-card ia-secao">
        <h3>Regras</h3>
        <p className="ia-suave">Situações em que a IA deve agir de um jeito definido. Ela segue estas regras antes de qualquer outra coisa.</p>
        <ul className="ia-regras">
          {config.aiRegras.map((regra, i) => (
            <li key={i} className="ia-regra">
              <label className="ia-regra__campo">
                <span>Quando o aluno…</span>
                <input
                  value={regra.quando}
                  onChange={(e) => mudarRegra(i, 'quando', e.target.value)}
                  placeholder="pedir para falar com um atendente"
                />
              </label>
              <label className="ia-regra__campo ia-regra__campo--largo">
                <span>a IA deve…</span>
                <input
                  value={regra.responder}
                  onChange={(e) => mudarRegra(i, 'responder', e.target.value)}
                  placeholder="informar o WhatsApp (00) 00000-0000, de segunda a sexta, das 9h às 18h"
                />
              </label>
              <button type="button" className="btn-ghost ia-icone" onClick={() => removerRegra(i)} aria-label={`Remover regra ${i + 1}`} title="Remover regra">
                <FiTrash2 aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn-secondary ia-botao" onClick={() => mudar('aiRegras', [...config.aiRegras, REGRA_VAZIA])}>
          <FiPlus aria-hidden="true" /> Adicionar regra
        </button>
      </section>

      <section className="qz-card ia-secao">
        <h3>O que a IA pode usar</h3>
        <label className="ia-opcao">
          <input type="checkbox" checked={config.aiUsarConteudoCursos} onChange={(e) => mudar('aiUsarConteudoCursos', e.target.checked)} />
          <span>
            <strong>Material do curso</strong>
            <span className="ia-suave">Tira dúvidas com base nas pílulas que o aluno já recebeu (sem adiantar as próximas).</span>
          </span>
        </label>
        <label className="ia-opcao">
          <input type="checkbox" checked={config.aiInformarDesempenho} onChange={(e) => mudar('aiInformarDesempenho', e.target.checked)} />
          <span>
            <strong>Desempenho do aluno</strong>
            <span className="ia-suave">Informa curso atual, etapa, acertos e notas quando o aluno perguntar.</span>
          </span>
        </label>
        <p className="ia-suave ia-nota">Sempre ativo: a IA nunca diz qual é a resposta da questão em aberto.</p>
        <p className="ia-suave ia-nota">
          Sempre ativo: a IA não conversa sobre assuntos fora dos cursos. Nesses casos o bot responde em uma frase e
          oferece os cursos (ou chama o aluno de volta ao curso em andamento).
        </p>
      </section>

      <section className="qz-card ia-secao">
        <h3>Atendimento humano</h3>
        <p className="ia-suave">
          Quando alguém da equipe responde um aluno pelo WhatsApp do bot (no celular ou no WhatsApp Web), o bot fica em silêncio
          com esse aluno: não responde, não corrige e não envia conteúdo. Cada nova mensagem da equipe renova a pausa.
        </p>
        <label className="ia-pausa">
          <span>Pausa de</span>
          <input
            type="number"
            min={0}
            max={1440}
            value={config.aiPausaAtendimentoMin}
            onChange={(e) => mudar('aiPausaAtendimentoMin', e.target.value)}
            aria-label="Minutos de pausa após mensagem da equipe"
          />
          <span>minutos (0 desliga)</span>
        </label>
      </section>

      <section className="qz-card ia-secao">
        <h3>Atendimento e atalhos</h3>
        <p className="ia-suave">
          O aluno conversa com a IA em linguagem normal. Ela entende o que ele quer e age sozinha: começa um curso
          (confirmando antes de trocar), continua de onde parou, pausa os envios do curso, retoma quando ele pedir e
          encerra o curso se ele desistir (confirmando antes). Na API oficial, as questões vão com o botão Responder;
          o aluno também pode escrever a letra ou a própria alternativa.
          Os atalhos abaixo continuam valendo e são respondidos sem passar pela IA.
        </p>
        <ul className="ia-comandos">
          <li><code>cursos</code><span>mostra a lista de cursos (o número escolhido começa o curso)</span></li>
          <li><code>1</code> / <code>2</code><span>respostas ao menu continuar / trocar de curso</span></li>
          <li><code>A</code>, <code>B</code>, <code>C</code>…<span>respondem a questão em aberto</span></li>
          {ativos.map((m) => (
            <li key={m.id}><code>{m.comando}</code><span>começa o curso “{m.nome}”</span></li>
          ))}
        </ul>
      </section>

      <div className="ia-salvar">
        <span className="ia-suave">{alterado ? 'Há alterações não salvas.' : 'Tudo salvo.'}</span>
        <button type="button" className="btn-primary ia-botao" onClick={async () => { if (await salvar()) toast.success('Treinamento salvo. Vale para as próximas mensagens.'); }} disabled={!alterado || salvando}>
          {salvando ? 'Salvando...' : 'Salvar treinamento'}
        </button>
      </div>

      <section className="qz-card ia-secao">
        <h3>Testar a IA</h3>
        <p className="ia-suave">Pergunte como se fosse um aluno. Escolha um aluno para a IA usar o curso e o desempenho dele. Alterações não salvas são salvas antes do teste.</p>
        <form className="ia-teste" onSubmit={testar}>
          <select aria-label="Simular como aluno" value={aluno} onChange={(e) => setAluno(e.target.value)}>
            <option value="">Sem aluno (só as regras da empresa)</option>
            {alunos.map((a) => <option key={a.contactId} value={a.contactId}>{a.rotulo}</option>)}
          </select>
          <div className="ia-teste__linha">
            <input
              aria-label="Mensagem de teste"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder="Ex.: Qual está sendo meu desempenho?"
              maxLength={1000}
            />
            <button type="submit" className="btn-primary ia-botao" disabled={testando || !mensagem.trim()}>
              <FiSend aria-hidden="true" /> {testando ? 'Pensando...' : 'Testar'}
            </button>
          </div>
        </form>
        {teste && (
          <div className="ia-conversa" aria-live="polite">
            <p className="ia-balao ia-balao--aluno">{teste.pergunta}</p>
            {teste.categoria === 'fora_do_escopo' && (
              <p className="ia-suave">Assunto fora dos cursos: em vez de conversar, o bot respondeu com as opções de curso.</p>
            )}
            <p className="ia-balao ia-balao--ia">{teste.resposta}</p>
            {teste.acoes && teste.acoes.length > 0 && (
              <p className="ia-suave">No WhatsApp, o bot também faria: {teste.acoes.join(', ')}. No teste, nada é enviado.</p>
            )}
            <details className="ia-prompt">
              <summary>Ver o que a IA recebeu</summary>
              <pre>{teste.prompt}</pre>
            </details>
          </div>
        )}
      </section>
    </div>
  );
}

export default TreinamentoIA;
